import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { ModalController, Platform, PopoverController } from '@ionic/angular/standalone';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
  IChargeSavedCardDto,
  IEmkanVoucherDetailsDto,
  IGenerateSadadBillDto,
  IGetEmkanVoucherDetailsDto,
  IPaymentDetailsDto,
  IPreparePaymentPageDto,
  IRedeemEmkanVoucherDto,
  IRequestEmkanOTPDto,
  ISTCPaymentAuthorizationDto,
  ISTCPaymentAuthorizeResponseDto,
  ISTCPaymentConfirmationDto,
  PaymentClassificationCodes,
  PaymentGateway,
  PaymentType,
  ServiceType,
  TranslationKeys,
} from 'src/app/contracts';
import { ClassificationCode, PaymentIdentifier } from 'src/app/contracts/app.types';
import { Api } from 'src/app/core/common/api';
import { InternalError } from 'src/app/core/contracts';
import { log } from 'src/app/core/helpers/logger';
import { UI } from 'src/app/core/helpers/ui';
import { PendingPaymentReminderService } from 'src/app/services/pending-payment-reminder.service';
import { EmkanModalComponent } from 'src/app/shared/components/old/payment/emkan/emkan.modal';
import { SadadPopoverComponent } from 'src/app/shared/components/old/payment/sadad/sadad.popover';
import { STCPaymentModalComponent } from 'src/app/shared/components/old/payment/stc/stc-payment.modal';
import { MarketingService } from './marketing.service';

export const mapClassificationToServiceType = (classificationCode: ClassificationCode) => {
  switch (classificationCode) {
    case PaymentClassificationCodes.Khidma:
      return ServiceType.Hourly;
    case PaymentClassificationCodes.Monthly:
      return ServiceType.Monthly;
    default:
      return ServiceType.All;
  }
};

export const getKhidmaPaymentIdentifier = (requestId: number) => `${PaymentClassificationCodes.Khidma}${requestId}`;
export const getMonthlyPaymentIdentifier = (requestId: number) => `${PaymentClassificationCodes.Monthly}${requestId}`;
export const getInstallmentPaymentIdentifier = (requestId: number) => `${PaymentClassificationCodes.Installment}${requestId}`;
export const getDelayRequestPaymentIdentifier = (requestId: number) => `${PaymentClassificationCodes.DelayRequest}${requestId}`;
export const getRequestId = (identifier: PaymentIdentifier) => Number(identifier.replace(/^[A-Za-z]+/, '')); // removes the classification code prefix, e.g. K (khidmah request), M (monthly request), I (installment request)
export const getPaymentClassificationCode = (identifier: PaymentIdentifier) => identifier.match(/^[A-Za-z]+/)?.[0] ?? ''; // removes the numeric part of the identifier, leaving only the classification code, e.g. K (khidmah request), M (monthly request), I (installment request)
export const getPaymentIdentifier = (classificationCode: ClassificationCode, requestId: number): PaymentIdentifier =>
  `${classificationCode}${requestId}` as PaymentIdentifier;

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private api = inject(Api);
  private ui = inject(UI);
  private platform = inject(Platform);
  private popoverCtrl = inject(PopoverController);
  private browser = inject(InAppBrowser);
  private modalCtrl = inject(ModalController);
  private router = inject(Router);
  protected reminderService = inject(PendingPaymentReminderService);
  marketing = inject(MarketingService);
  host: string;

  constructor() {
    this.host = this.platform.is('capacitor') ? 'commaharahapp://payment' : window.location.origin;
  }
  public static isHourly(classificationCode?: ClassificationCode) {
    return classificationCode === PaymentClassificationCodes.Khidma;
  }
  public static isMonthly(classificationCode?: ClassificationCode) {
    return classificationCode === PaymentClassificationCodes.Monthly;
  }
  public static isInstallment(classificationCode?: ClassificationCode) {
    return classificationCode === PaymentClassificationCodes.Installment;
  }
  public static isDelayRequest(classificationCode: ClassificationCode) {
    return classificationCode === PaymentClassificationCodes.DelayRequest;
  }
  public activateRequest(requestId: number, classificationCode: ClassificationCode, details: IPaymentDetailsDto): Observable<void> {
    let stream: Observable<never> | undefined;
    switch (classificationCode) {
      case PaymentClassificationCodes.Khidma:
        stream = this.api.post(`hourly/${requestId}/activate`);
        break;
      case PaymentClassificationCodes.Monthly:
        stream = this.api.post(`monthly/${requestId}/activate`);
        break;
      case PaymentClassificationCodes.Installment:
        stream = this.api.post(`installments/${requestId}/activate`);
        break;
      case PaymentClassificationCodes.DelayRequest:
        stream = this.api.post(`hourly/visits/activate-delay-request/${requestId}`);
        break;
      // default: throw new InternalError(`Payment classification not supported: '${classificationCode}'`); causes: Invalid type "never" of template literal expression
    }
    if (!stream) {
      throw new InternalError(`Payment classification not supported: '${classificationCode}'`);
    }
    return stream.pipe(
      tap(() => {
        this.postPaymentRedirect(requestId, classificationCode, details);
      })
    );
  }
  public chargeSavedCard(
    requestId: number,
    classificationCode: ClassificationCode,
    cardId: number,
    gateway: PaymentGateway,
    usingWallet: boolean,
    details: IPaymentDetailsDto
  ): Observable<unknown> {
    const identifier = `${classificationCode}${requestId}`;
    return this.api.post('services/charge-saved-card', { identifier, gateway, cardId, usingWallet } as IChargeSavedCardDto).pipe(
      tap(() => {
        this.postPaymentRedirect(requestId, classificationCode, details);
      })
    );
  }
  public pay(
    paymentType: PaymentType,
    requestId: number,
    classificationCode: ClassificationCode,
    usingWallet: boolean,
    saveCard: boolean,
    details: IPaymentDetailsDto
  ): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      const identifier = `${classificationCode + requestId}`;
      switch (paymentType) {
        case PaymentType.SADAD:
          this.api.post('services/generate-sadad-bill', { identifier, usingWallet } as IGenerateSadadBillDto).subscribe(async billNumber => {
            const popover = await this.popoverCtrl.create({
              component: SadadPopoverComponent,
              componentProps: {
                identifier,
                billNumber,
              },
              cssClass: 'wide-popover',
              backdropDismiss: false,
            });
            await popover.present();
            await popover.onDidDismiss();
            this.postPaymentRedirect(requestId, classificationCode, details);
            resolve(false);
          });
          break;
        case PaymentType.STCPay:
          this.modalCtrl.create({ component: STCPaymentModalComponent, componentProps: { identifier, usingWallet } }).then(async modal => {
            await modal.present();
            const { data: paid } = await modal.onDidDismiss();
            if (paid) {
              // true if paid successfully
              this.postPaymentRedirect(requestId, classificationCode, details);
              resolve(true);
            } else {
              resolve(false);
            }
          });
          break;
        case PaymentType.Emkan:
          this.modalCtrl.create({ component: EmkanModalComponent, componentProps: { requestId, classificationCode, usingWallet } }).then(async modal => {
            await modal.present();
            const { data: paid } = await modal.onDidDismiss();
            if (paid) {
              this.postPaymentRedirect(requestId, classificationCode, details);
              resolve(true);
            } else {
              this.postPaymentRedirect(requestId, classificationCode, null); // post payment redirect is required after a failed emkan payment, however no log purchase event should be submitted hence passing null for details
              resolve(false);
            }
          });
          break;
        default:
          /* case PaymentType.CreditCard: case PaymentType.Mada: case PaymentType.ApplePay: case PaymentType.Tamara: case PaymentType.Urpay: case PaymentType.Amex: */
          this.api
            .post<string>('services/create-pay-page', { identifier, host: this.host, usingWallet, paymentType, saveCard } as IPreparePaymentPageDto)
            .subscribe(url => {
              if (!this.platform.is('capacitor')) {
                log({ msg: '@payment.service: On browser payment' });
                this.onBrowserPayment(url, requestId, classificationCode, details);
              } else {
                log({ msg: '@payment.service: On device payment' });
                this.onDevicePayment(url).then(paid => {
                  if (paid) {
                    resolve(true);
                    this.postPaymentRedirect(requestId, classificationCode, details);
                  } else {
                    resolve(false);
                  }
                });
              }
            });
          break;
      }
    });
  }

  public postPaymentRedirect(requestId: number, classificationCode: ClassificationCode, details: IPaymentDetailsDto) {
    this.marketing.logPurchase(requestId, classificationCode, details);
    this.reminderService.cancelLocalReminder(requestId);
    if (PaymentService.isHourly(classificationCode)) {
      this.router.navigate(['/hourly/new/success/' + requestId, { payment_result: 'success' }]);
    } else if (PaymentService.isMonthly(classificationCode)) {
      this.router.navigate(['/monthly/new/success/' + requestId, { payment_result: 'success' }]);
    } else if (PaymentService.isInstallment(classificationCode)) {
      this.router.navigate(['/tabs/contracts']);
    } else if (PaymentService.isDelayRequest(classificationCode)) {
      this.router.navigate(['/tabs/contracts']);
    } else {
      this.router.navigate(['/tabs/home']);
    }
  }
  private onDevicePayment(paymentUrl: string): Promise<boolean> {
    return new Promise(resolve => {
      this.ui.showLoading(TranslationKeys.Payment.LoadingPaymentPage);
      const browser = this.browser.create(paymentUrl, '_blank', {
        location: 'no',
        hardwareback: 'no',
        hidden: 'yes',
        // hidden: isIOS ? 'no' : 'yes',
        /* inappbrowser bug https://github.com/apache/cordova-plugin-inappbrowser/issues/649
                   calling browser.close(); on iOS won't trigger exit event if the browser was hidden
                */
        useWideViewPort: 'no',
        zoom: 'no',
        closebuttoncaption: this.ui.translate.instant(TranslationKeys.Common.Back),
      });

      browser.on('loadstart').subscribe(event => {
        this.ui.hideLoading();
        if (this.platform.is('ios')) {
          // loadstart not working properly for android
          if (event.url.startsWith(`commaharahapp`)) {
            browser.close();
            /* normalize url to support URLSearchParams
                           parameters are formatted for angular (i.e. using ';') e.g: new-booking/details;packageId=2;addressId=318 */
            const urlParams = new URLSearchParams(event.url.replace(/;/g, '&'));
            const result = urlParams.get('payment_result');
            const isPaid = result === 'success';
            if (!isPaid && result != 'Cancelled') {
              // no need to show the Cancelled message, the user already knows
              this.ui.showError(result || '');
            }
            resolve(isPaid);
          }
        } else {
          // only hide on android
          // don't hide the browser on iOS, otherwise loadstop won't fire (it fires the first time only, but never again)
          browser.hide();
        }
      });
      browser.on('loadstop').subscribe(() => {
        browser.show();
      });
      if (this.platform.is('android')) {
        // loaderror not working properly for ios
        browser.on('loaderror').subscribe(event => {
          if (event.url.startsWith(`commaharahapp`)) {
            this.ui.hideLoading();
            browser.close();
            /* normalize url to support URLSearchParams
                           parameters are formatted for angular (i.e. using ';') e.g: new-booking/details;packageId=2;addressId=318 */
            const urlParams = new URLSearchParams(event.url.replace(/;/g, '&'));
            const result = urlParams.get('payment_result');
            const isPaid = result === 'success';
            if (!isPaid) {
              this.ui.showError(result || '');
            }
            resolve(isPaid);
          }
        });
      }
    });
  }
  private async onBrowserPayment(paymentUrl: string, requestId: number, classificationCode: ClassificationCode, paymentDetails: IPaymentDetailsDto) {
    if (paymentDetails) {
      await this.marketing.savePendingPaymentContext(requestId, classificationCode, paymentDetails);
    }
    window.open(paymentUrl, '_self');
  }
  public authorizeSTCPayment(identifier: string, mobile: string, usingWallet: boolean) {
    return this.api.post<ISTCPaymentAuthorizeResponseDto>('services/authorize-stcpayment', { identifier, mobile, usingWallet } as ISTCPaymentAuthorizationDto);
  }
  public confirmSTCPayment(code: string, authorizeResponse: ISTCPaymentAuthorizeResponseDto, usingWallet: boolean) {
    return this.api.post('services/confirm-stcpayment', {
      otpValue: code,
      otpReference: authorizeResponse.otpReference,
      stcPayPmtReference: authorizeResponse.stcPayPmtReference,
      usingWallet,
    } as ISTCPaymentConfirmationDto);
  }

  public getEmkanVoucherAmount(voucherCode: string, applicationId: string): Observable<number> {
    return this.api
      .post<IEmkanVoucherDetailsDto>('services/emkan/voucher-details', { voucherCode, applicationId } as IGetEmkanVoucherDetailsDto)
      .pipe(map((voucher: IEmkanVoucherDetailsDto) => voucher.amount));
  }
  public requestEmkanVoucherOTP(voucherCode: string, applicationId: string, identifier: string): Observable<void> {
    return this.api.post('services/emkan/request-voucher-otp', { voucherCode, applicationId, identifier } as IRequestEmkanOTPDto);
  }
  public redeemEmkanVoucher(voucherCode: string, applicationId: string, otp: string, identifier: string): Observable<void> {
    return this.api.post('services/emkan/redeem-voucher', { voucherCode, applicationId, otp, identifier } as IRedeemEmkanVoucherDto);
  }
}
